import type { EventBookingClosedCta } from "@/db/schema/event";
import { EVENT_BOOKING_CLOSED_CTA } from "@/db/schema/event";

/**
 * What a closed booking window says, and whether the event stays in discovery.
 *
 * One table, read by the event page footer, the /hiives rail, the hiive page
 * and the queen form, so a preset cannot mean "walk-ins welcome" in one place
 * and something else in another.
 *
 * `keepInDiscovery` is the half that is not copy. An event nobody can book is
 * a dead end in a browse rail, so the default is to drop it — but an organiser
 * who has explicitly said "just turn up" is telling us the opposite, and the
 * one case that prompted all of this was exactly that: bookings had closed, the
 * link was still being shared, and the only thing on screen was a grey pill
 * reading "Booking unavailable".
 */
export interface BookingClosedCtaPreset {
  /** Replaces "Bookings for this event are currently closed." */
  headline: string;
  /** Shown to the organiser in the picker. */
  optionLabel: string;
  /** One line under the picker explaining what visitors will see. */
  optionHint: string;
  /** Short form for a discovery card, where there is room for two words. */
  cardBadge: string;
  /** Whether browse surfaces keep listing the event once bookings close. */
  keepInDiscovery: boolean;
}

export const BOOKING_CLOSED_CTA_PRESETS: Record<
  EventBookingClosedCta,
  BookingClosedCtaPreset
> = {
  walk_ins: {
    headline: "Online bookings have closed — walk-ins welcome",
    optionLabel: "Walk-ins welcome",
    optionHint:
      "Online booking is over, but people can still turn up and pay at the door. Keeps the event listed in discovery.",
    cardBadge: "Walk-ins welcome",
    keepInDiscovery: true,
  },
  sold_out: {
    headline: "Sold out — every ticket is gone",
    optionLabel: "Sold out",
    optionHint:
      "There is nothing left to sell. Stays listed so people can see it filled up and follow the next one.",
    cardBadge: "Sold out",
    keepInDiscovery: true,
  },
  reopening: {
    headline: "Bookings are closed for now — the next round opens soon",
    optionLabel: "Reopening soon",
    optionHint:
      "This round is done and another is coming. Stays listed so returning visitors find it.",
    cardBadge: "Reopening soon",
    keepInDiscovery: true,
  },
  finished: {
    headline: "This event has finished",
    optionLabel: "Finished",
    optionHint:
      "Nothing more to book and nothing more to come. The page stays reachable by link, but the event leaves discovery.",
    cardBadge: "Finished",
    keepInDiscovery: false,
  },
};

export const BOOKING_CLOSED_CTA_OPTIONS = EVENT_BOOKING_CLOSED_CTA.map(
  (value) => ({ value, ...BOOKING_CLOSED_CTA_PRESETS[value] }),
);

export function isBookingClosedCta(
  value: unknown,
): value is EventBookingClosedCta {
  return (
    typeof value === "string" &&
    (EVENT_BOOKING_CLOSED_CTA as readonly string[]).includes(value)
  );
}

export function bookingClosedCtaPreset(
  cta: EventBookingClosedCta | null | undefined,
): BookingClosedCtaPreset | null {
  return cta ? BOOKING_CLOSED_CTA_PRESETS[cta] : null;
}

/**
 * Does a closed event still belong in a browse rail?
 *
 * Only an organiser can answer that, so the answer is only ever yes when one
 * of them picked a preset that says so.
 */
export function keepsClosedEventInDiscovery(
  cta: EventBookingClosedCta | null | undefined,
): boolean {
  return bookingClosedCtaPreset(cta)?.keepInDiscovery ?? false;
}

/**
 * The two lines a closed event shows in its footer.
 *
 * `message` is the organiser's own sentence and is deliberately allowed to
 * stand alone: somebody who typed "Cash only at the door, ask for Ravi" has
 * said everything the visitor needs, and pairing it with a preset headline is
 * what keeps it from reading as a system error.
 */
export function bookingClosedCopy({
  cta,
  message,
  fallbackHeadline,
}: {
  cta: EventBookingClosedCta | null | undefined;
  message: string | null | undefined;
  /** Today's generated sentence, used when no preset was chosen. */
  fallbackHeadline: string;
}): { headline: string; message: string | null } {
  const preset = bookingClosedCtaPreset(cta);
  const trimmed = message?.trim();

  return {
    headline: preset?.headline ?? fallbackHeadline,
    message: trimmed && trimmed.length > 0 ? trimmed : null,
  };
}
